import * as React from "react";
import { useFormContext } from "react-hook-form";
import objectHash from "object-hash";
import { DomainInput, DomainDryRun as IDomainDryRun } from "./types/input";
import { useDebounce } from "../../../components/table/useDebounce";
import { DomainDryRun } from "./domainDryRun";
import { processDryRunResponse } from "../../../mobxStores/dryRun/mk8s";
import { parsedDomain } from "../../../services/utils";
import { homeLink, request, resourceLink } from "../../../services/cpln";
import { getDefaultDomainInput, inputToDomain } from "./utils/utils";

export const CreateNGDryRunChecker: React.FC = () => {
  const { watch, setValue, getValues } = useFormContext<DomainInput>();

  const mode = watch("mode") || "basic";
  const isEdit = watch("isEdit") || false;
  const dryRun = watch("dryRun.raw.value") || {};
  const dryRunNameOnly = watch("dryRun.nameOnly.value") || {};

  const basicName = watch("basic.name") || "";
  const basicWorkloadLink = watch("basic.workload.link") || "";
  const advancedName = watch("advanced.name") || "";
  const dryRunTriggerer = watch("dryRun.triggerer") || "";

  const [debouncedName, setDebouncedName] = useDebounce(advancedName, 300);
  React.useEffect(() => {
    setDebouncedName(advancedName);
  }, [advancedName]);
  React.useEffect(() => {
    checkDryRunNameOnly();
  }, [debouncedName]);

  const [allValues, setAllValues] = React.useState<DomainInput>(getDefaultDomainInput());
  const [allValuesHash, setAllValuesHash] = React.useState("");
  const [lastDryRunHash, setLastDryRunHash] = React.useState("");

  React.useEffect(() => {
    const intervalId = setInterval(() => {
      let _allValues = getValues();
      _allValues = JSON.parse(JSON.stringify(_allValues));
      delete (_allValues as any).dryRun;
      setAllValues(_allValues);
    }, 1000);
    return () => clearInterval(intervalId);
  }, [getValues]);

  React.useEffect(() => {
    setAllValuesHash(objectHash(allValues));
  }, [allValues]);

  React.useEffect(() => {
    if (allValuesHash !== lastDryRunHash) {
      checkDryRun();
    }
  }, [allValuesHash, lastDryRunHash]);

  React.useEffect(() => {
    checkDryRun();
    checkDryRunNameOnly();
    checkDryRunApex();
  }, [dryRunTriggerer]);

  async function checkDryRunNameOnly() {
    try {
      if ((mode === "basic" && !basicName) || (mode === "advanced" && !advancedName)) {
        setValue("dryRun.nameOnly.value", {});
        return;
      }
      setValue("dryRun.nameOnly.isLoading", true);

      const method = isEdit ? "patch" : "post";
      const name = mode === "basic" ? basicName : advancedName;
      const url = isEdit ? resourceLink("domain", name) : homeLink("domain");

      let { data } = await request({
        method: method,
        url: url + "?dryRun",
        body: { kind: "domain", name: mode === "basic" ? basicName : advancedName },
      });

      if (method === "patch") {
        setValue("dryRun.nameOnly.value", { status: 200 });
      } else {
        setValue("dryRun.nameOnly.value", processDryRunResponse(data as any) as any);
      }
      setValue("dryRun.nameOnly.isLoading", false);
    } catch (e) {
      setValue("dryRun.nameOnly.isLoading", false);
      let dryRun: IDomainDryRun = e?.response?.data;
      setValue("dryRun.nameOnly.value", processDryRunResponse(dryRun as any) as any);
    }
  }

  const domainBasicBody = React.useMemo(() => {
    const domain: any = {
      kind: "domain",
      name: basicName,
    };
    domain.spec = {
      dnsMode: "cname",
      certChallengeType: "http01",
      ports: [
        {
          number: 443,
          protocol: "http2",
          routes: [
            {
              workloadLink: basicWorkloadLink,
              prefix: "/",
            },
          ],
        },
      ],
    };
    return domain;
  }, [basicName, basicWorkloadLink]);

  async function checkDryRun() {
    const hash = allValuesHash;
    try {
      if ((mode === "basic" && !basicName) || (mode === "advanced" && !advancedName)) {
        setValue("dryRun.raw.value", {});
        return;
      }
      setValue("dryRun.raw.isLoading", true);

      const method = isEdit ? "patch" : "post";
      const name = mode === "basic" ? basicName : advancedName;
      const url = isEdit ? resourceLink("domain", name) : homeLink("domain");
      const body = mode === "basic" ? domainBasicBody : inputToDomain(getValues());

      let { data } = await request({
        method: method,
        url: url + "?dryRun",
        body: body,
      });

      const processed = processDryRunResponse(data as any);
      setValue("dryRun.raw.value", processed as any);
      setValue("dryRun.raw.isLoading", false);
    } catch (e) {
      setValue("dryRun.raw.isLoading", false);
      const dryRun: IDomainDryRun = e?.response?.data;
      const processed = processDryRunResponse(dryRun as any);
      setValue("dryRun.raw.value", processed as any);
      if (e?.response?.data.code === "apex_must_exist") {
        checkDryRunApex();
      }
    }
    setLastDryRunHash(hash);
  }

  async function checkDryRunApex() {
    try {
      if (!advancedName) {
        setValue("dryRun.apex.value", {});
        return;
      }
      setValue("dryRun.apex.isLoading", true);
      let { data } = await request({
        method: "post",
        url: homeLink("domain") + "?dryRun",
        body: { name: parsedDomain(advancedName).domain },
      });

      setValue("dryRun.apex.value", processDryRunResponse(data as any) as any);
      setValue("dryRun.apex.isLoading", false);
    } catch (e) {
      setValue("dryRun.apex.isLoading", false);
      let dryRun: IDomainDryRun = e?.response?.data;
      setValue("dryRun.apex.value", processDryRunResponse(dryRun as any) as any);
    }
  }

  return (
    <>
      {mode === "advanced" && dryRunNameOnly.status === 200 && dryRun.code !== "apex_must_exist" ? (
        <DomainDryRun dryRunPath="raw" showValid={true} />
      ) : null}
    </>
  );
};
